import { useEffect, useCallback, useRef } from 'react';
import { useChatData } from '../context/ChatContext';
import type { ChatMessage } from '../context/chatTypes';

export function useMessages(conversationId: string | null) {
  const {
    messages,
    loadingMessages,
    hasMoreMessages,
    loadMessages,
    loadOlderMessages,
  } = useChatData();

  // Track which conversations have had their full history loaded,
  // so we don't skip loading just because the stream appended a message.
  const loadedRef = useRef<Set<string>>(new Set());

  const conversationMessages: ChatMessage[] = conversationId
    ? messages[conversationId] || []
    : [];

  const isLoading = conversationId
    ? loadingMessages[conversationId] || false
    : false;

  const hasMore = conversationId
    ? hasMoreMessages[conversationId] !== false
    : false;

  // Load messages when conversation changes
  useEffect(() => {
    if (conversationId && !loadedRef.current.has(conversationId)) {
      loadedRef.current.add(conversationId);
      loadMessages(conversationId);
    }
  }, [conversationId, loadMessages]);

  const loadMore = useCallback(() => {
    if (conversationId && hasMore && !isLoading) {
      loadOlderMessages(conversationId);
    }
  }, [conversationId, hasMore, isLoading, loadOlderMessages]);

  const refresh = useCallback(() => {
    if (conversationId) {
      loadMessages(conversationId);
    }
  }, [conversationId, loadMessages]);

  return {
    messages: conversationMessages,
    isLoading,
    hasMore,
    loadMore,
    refresh,
  };
}
